import React from 'react';
import { initials, pickAvatarGradient } from './utils';
import { getPatientPageVisitPathSegment } from '../../utils/visitPathUtils';
import { convertScheduledTime } from '../../utils/scheduleTimeDisplay';
import { foreignSchemaOf } from '../../utils/rowSchema';
import { supabaseSchema } from '../../components/supabaseClient';

// Map schedule-row fields → patient-card display fields. notes-c stores actual columns
// like `patient_name`, `EHR_ID`, `date_`, `dob`, `type`. We surface whichever id is
// canonical for the navigation route (the DB uuid `id`).
function getCardUuid(row) {
  return getPatientPageVisitPathSegment(row) || row?.uuid || row?.EHR_ID || null;
}

// Age calculated from DOB (notes-c doesn't store an `age` column).
function calcAge(dob, visitDateStr) {
  if (!dob) return null;
  const birth = new Date(dob);
  const visit = visitDateStr ? new Date(visitDateStr) : new Date();
  if (Number.isNaN(birth.getTime())) return null;
  let age = visit.getFullYear() - birth.getFullYear();
  const m = visit.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && visit.getDate() < birth.getDate())) age--;
  return age;
}

function shortTime(t) {
  if (!t) return '';
  return t.toString().slice(0, 5);  // 09:00:00 → 09:00
}

// Mockup color palette for known condition tags; everything else uses the default slate.
const CHIP_PALETTE = {
  'Sleep Apnea':    'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300',
  'BMI 36.3':       'bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-300',
  'GLP-1 Interest': 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'Urgent':         'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300',
  'New Patient':    'bg-cyan-100 dark:bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  'Signed ✓':       'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  'Follow-up':      'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};
const DEFAULT_CHIP = 'bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200';

export default function PatientCard({ patient, active, completed, onClick, viewerTimezone, hasPriorVisit = false }) {
  if (!patient) return null;
  const tags = patient.tags || [];
  // notes-c canonical field mapping
  const mrn = patient.mrn || patient.EHR_ID || '';
  const age = patient.age ?? calcAge(patient.dob, patient.date_);
  const sex = patient.sex || patient.gender || '';
  // Convert the owner's stored wall-clock time into the viewer's timezone — same
  // as the schedules table (convertScheduledTime + timezone_source + viewerTimezone).
  // `appointment_time` is a pre-formatted override (mockups/tests); fall back to it
  // (then the raw slice) only when there's no convertible time_.
  const convertedTime = convertScheduledTime(
    patient.date_,
    patient.time_,
    patient.timezone_source ?? null,
    viewerTimezone,
  );
  const time =
    convertedTime && convertedTime !== 'N/A'
      ? convertedTime
      : patient.appointment_time || shortTime(patient.time_);
  const meta = [mrn && `#${mrn}`, age != null && `${age}${sex ? sex.toString().charAt(0).toUpperCase() : ''}`]
    .filter(Boolean).join(' · ');
  // Only a LINKED PROVIDER's row gets a chip. `_schema` is present on every row
  // once the sidebar fans out, including the viewer's own, so comparing against
  // the home schema is what keeps the chip meaningful instead of stamping it on
  // every card.
  const foreignSchema = foreignSchemaOf(patient, supabaseSchema);
  // The ROW is handed back with the uuid: a cross-schema row carries `_schema`,
  // and without it the caller cannot tell the chart which schema to open in.
  return (
    <button
      type="button"
      onClick={() => onClick?.(getCardUuid(patient), patient)}
      aria-pressed={!!active}
      data-completed={completed ? 'true' : 'false'}
      className={[
        'w-full text-left p-3 rounded-xl mb-1.5 group',
        active
          ? 'bg-blue-50/70 dark:bg-blue-500/[0.08] border border-blue-200 dark:border-blue-500/30'
          : 'border border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/40',
        completed ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 rounded-full ${pickAvatarGradient(patient, completed)} flex items-center justify-center text-white text-xs font-bold shrink-0`}
          data-avatar-completed={completed ? 'true' : 'false'}
        >
          {initials(patient.patient_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-semibold text-sm truncate text-slate-900 dark:text-slate-100 flex items-center gap-1.5 min-w-0">
              <span className="truncate">{patient.patient_name}</span>
              {/* Prior-visit indicator — surfaces at-a-glance which patients
                  have been seen before so the clinician can grab the chart
                  with the right context (history loaded, copy-from-prior
                  template offered, etc.). Hidden in completed bucket —
                  they already know — and on roster rows that have no
                  date-keyed history. */}
              {hasPriorVisit && (
                <span
                  data-prior-visit-badge
                  title="This patient has prior visits on file"
                  aria-label="Has prior visits"
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 shrink-0"
                >
                  <svg
                    className="w-2.5 h-2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </span>
              )}
            </div>
            <div className="text-xs font-mono text-slate-500 dark:text-slate-400 shrink-0">{time}</div>
          </div>
          {meta && (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{meta}</div>
          )}
          {/* Whose patient this is. Only for a linked provider's row — the same
              indigo chip ResultsTable and the schedules list use, so the sidebar
              reads consistently with the pages the row also appears on. Without
              it a doctor working across a clinic group cannot tell their own
              patients from a colleague's. */}
          {foreignSchema && (
            <div className="mt-1">
              <span
                data-provider-chip
                title={`This visit is in the ${foreignSchema} schema`}
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/[0.15] dark:text-indigo-300"
              >
                {foreignSchema}
              </span>
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {tags.map((tag, tagIdx) => (
                <span
                  key={`${tagIdx}-${tag}`}
                  data-chip
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${CHIP_PALETTE[tag] || DEFAULT_CHIP}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
